import React, { useCallback, useRef, useState } from 'react';
import {
  ButtonNewRecord,
  FlexRow,
  TableEditButtons,
  toastFailure,
  usePaginatedQueryV8,
} from 'motion-components';
import { Card } from 'react-bootstrap';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { gqlGetQuery } from '../graphql';
import { buildWhereFromFilterValues, Filters } from './filters';
import { DataGrid } from './grid';
import { handleExport } from './export-columns';
import { useApolloClient } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserLocationPermissions } from 'shared-pages/hooks';
import { isFotokiklosi } from 'shared-pages/common/constants';

/* ----------------------------------------------------------- */
/* ------------------------ Page ----------------------------- */
export const UsersGridPage = () => {
  const { appStore } = useRootStore();
  const { handleGraphqlError } = useHandleError('MaterialsGridPage');

  const location = useLocation<any>();
  const history = useHistory();
  const [selected, setSelected] = useState<any>(null);
  const { t } = useTranslation();

  const { canView, canAddNew, canEdit, canDelete } = userPermissions();
  function userPermissions() {
    if (isFotokiklosi) {
      if (appStore.isPlatformOwner)
        return { canView: true, canAddNew: true, canEdit: true, canDelete: true };
      else return { canView: true, canAddNew: false, canEdit: false, canDelete: false };
    } else {
      if (appStore.isPlatformOwner)
        return { canView: true, canAddNew: true, canEdit: true, canDelete: true };
      else if (appStore.isCollector)
        return { canView: true, canAddNew: true, canEdit: true, canDelete: true };
      else if (appStore.isCollectorDriver)
        return { canView: true, canAddNew: false, canEdit: false, canDelete: false };
      else if (appStore.isContributor)
        return { canView: true, canAddNew: true, canEdit: true, canDelete: true };
      else if (appStore.isWasteReceptionPoint)
        return { canView: true, canAddNew: true, canEdit: true, canDelete: true };
      else return { canView: true, canAddNew: false, canEdit: false, canDelete: false };
    }
  }

  const handleAddNew = () => {};
  /* ------------------- Query ------------------------------- */
  const initOrderBy = 'userName ASC';
  const query = usePaginatedQueryV8(
    gqlGetQuery,
    buildWhereFromFilterValues,
    location.state?.order ?? initOrderBy,
    appStore,
    true,
    { isActive: true },
  );
  const {
    loading,
    data,
    dataCount,
    error,
    initPageIndex,
    filtersChanged,
    gridPageChanged,
    queryState,
    queryParams,
    setQueryParams,
    refetch,
  } = query;

  const client = useApolloClient();

  const handleCreateNew = useCallback(() => {
    history.push({
      pathname: generatePath(getRouteByName('User'), { id: -1 }),
      state: {
        pathname: location.pathname,
        ...location.state,
      },
    });
  }, [history, location.pathname, location.state]);

  const gridRef = useRef();

  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  } else {
    return (
      <Card className="shadow">
        <Card.Header>
          <FlexRow>
            <Card.Title className="m-0">{t('Χρήστες')}</Card.Title>
            {/* Table Add New Button  */}
            {canAddNew && (
              <ButtonNewRecord onClick={handleCreateNew} size="sm" label={t('Νέος Χρήστης')} />
            )}
          </FlexRow>
        </Card.Header>
        <Card.Body className="nopadding">
          {/* Φίλτρα */}
          <Filters onChange={filtersChanged} initialValues={queryState?.filterValues ?? {}} />
          {/* Grid */}
          <DataGrid
            ref={gridRef}
            loading={loading}
            data={data}
            refetch={refetch}
            dataCount={dataCount}
            initPageIndex={initPageIndex}
            onPageChange={gridPageChanged}
            handleExport={() => {
              handleExport(client, queryState, t);
            }}
            initOrderBy={queryParams?.order ?? initOrderBy}
            onOrderChange={(orderBy) => {
              const newQueryParams = { ...queryParams, order: orderBy };
              setQueryParams(newQueryParams);
              history.replace({
                pathname: location.pathname,
                state: {
                  ...location.state,
                  order: orderBy,
                },
              });
            }}
            selected={selected}
            setSelected={setSelected}
          />
        </Card.Body>
      </Card>
    );
  }
};
